import Link from "next/link";
import moment from "moment";
import { GACustomEvent } from "src/utils/analytics";
import { Link as MagnoliaLink } from "src/components/Magnolia";
import { containPromoHref } from "src/utils/magnolia";

type CardContainerProps = {
  img?: string;
  date?: Date;
  href?: string;
  slug?: string;
  button?: string;
  withButton?: boolean;
  buttonSize?: string;
  title?: string;
  titleFontFamily?: string;
  titleFontSize?: string;
  titleFontSizeMobile?: string;
  subtitle?: string;
  category?: string;
  width?: number;
  dataCy?: string;
};

export const CardContainer = ({
  img,
  date,
  href,
  slug,
  button = "Lihat",
  withButton = true,
  buttonSize = null,
  title,
  titleFontFamily,
  titleFontSize,
  titleFontSizeMobile,
  subtitle,
  category,
  width,
  dataCy,
}: CardContainerProps) => {
  const isPromoHref = containPromoHref(href);
  const ComponentLink = isPromoHref ? MagnoliaLink : Link;

  return (
    <>
      {withButton ? (
        <ComponentLink href={slug} legacyBehavior>
          <a data-cy={dataCy} style={{ height: "100%" }}>
            <div className="card-container">
              <div className="card-image">
                {width && width > 600
                  ? img && (
                      <img
                        data-src={img + "?w=" + width * 0.4}
                        src={img + "?w=" + width * 0.4}
                        alt={slug}
                        className="card-banner"
                      />
                    )
                  : img && (
                      <img
                        data-src={img + "?w=" + width * 1.5}
                        src={img + "?w=" + width * 1.5}
                        alt={slug}
                        className="card-banner"
                      />
                    )}
              </div>
              <div className="card-desc-container">
                <div className="card-description">
                  {title && <p className="card-title">{title}</p>}
                  <p className="card-subtitle">{subtitle}</p>
                </div>
                <div className="card-additional">
                  {date ? (
                    <div className="card-datetime">
                      <strong>{category}</strong>
                      {category ? (
                        <div className="card-separator">&nbsp; | &nbsp;</div>
                      ) : (
                        ""
                      )}

                      {moment(date).format("LL")}
                    </div>
                  ) : (
                    <div />
                  )}
                  <span>
                    <div
                      className={`card-button ${
                        buttonSize === "Large" ? "card-button--large" : ""
                      }`}
                      onClick={() =>
                        GACustomEvent("deals_page_find_out_more", "click")
                      }
                    >
                      {button}
                    </div>
                  </span>
                </div>
              </div>
            </div>
          </a>
        </ComponentLink>
      ) : (
        <div className="card-container">
          <div className="card-desc-container">
            <div className="card-description">
              {title && <p className="card-title">{title}</p>}
              <p className="card-subtitle">{subtitle}</p>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .card-description {
          min-height: 80px;
          padding: 16px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }
        .card-additional {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px;
        }
        .card-additional .card-datetime {
          font-size: 14px;
        }
        .card-additional .card-datetime .card-separator {
          color: var(--color-red);
          font-weight: bold;
        }
        .card-button {
          background-color: var(--color-red);
          color: var(--color-white);
          display: flex;
          padding: 10px;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          font-weight: bold;
        }
        .card-button--large {
          padding: 8px 40px;
        }
        .card-title {
          font-family: ${titleFontFamily ? titleFontFamily : ""};
          font-size: ${titleFontSize ? titleFontSize : "18px"};
          font-weight: bold;
          margin: 0 0 8px;
        }
        .card-subtitle {
          font-size: 16px;
          margin: 0;
        }
        .card-container {
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          height: 100%;
          max-width: 432px;
          flex: 1;
          width: 100%;
          flex: 1;
          border-radius: 8.33px;
          background-color: var(--color-white);
          box-shadow: 0 3px 10px 0 var(--color-white-dark);
          align-self: auto;
          margin-left: auto;
          margin-right: auto;
        }
        .card-image {
          width: 100%;
        }
        .card-image img {
          width: 100%;
          border-radius: 8.33px 8.33px 0px 0px;
        }
        .card-desc-container {
          min-height: 200px;
          height: 100%;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }
        @media only screen and (max-width: 600px) {
          .card-title {
            font-size: ${titleFontSizeMobile ? titleFontSizeMobile : "14px"};
          }
          .card-subtitle {
            font-size: 13px;
          }
          .card-description {
            min-height: 70px;
          }
          .card-desc-container {
            min-height: auto;
          }
          .card-button {
            width: auto;
            height: auto;
            padding: 5px 25px;
          }
        }
      `}</style>
    </>
  );
};
